/**
 * StrategyPicker — dropdown that lets Elite users pick a scoring strategy
 * or create a custom one. Non-Elite users see the picker but see a Lock
 * icon and a click routes to /pricing.
 *
 * Props:
 *   - value: current strategy id ("balanced", "custom_<id>", ...)
 *   - onChange: (id, weights) => void
 *   - isElite: gate writes/customs behind Elite
 *   - onUpgrade: () => void when a non-Elite tries to open the editor
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronDown, Plus, Lock, Sliders as SlidersIcon, Crown } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import StrategyEditor from "./StrategyEditor";
import { API } from "../lib/helpers";

export default function StrategyPicker({ value = "balanced", onChange, isElite = false, onUpgrade }) {
  const [open, setOpen] = useState(false);
  const [strategies, setStrategies] = useState({ presets: [], custom: [] });
  const [meta, setMeta] = useState({ can_create: false, is_pro: false, is_elite: false, max_custom: 0 });
  const [editing, setEditing] = useState(null); // strategy object or "new"
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/strategies`);
      setStrategies({ presets: r.data.presets || [], custom: r.data.custom || [] });
      setMeta({
        can_create: !!r.data.can_create,
        is_pro: !!r.data.is_pro,
        is_elite: !!r.data.is_elite,
        max_custom: r.data.max_custom ?? 0,
      });
    } catch {
      // fall back to presets only
      try {
        const r = await axios.get(`${API}/strategies/presets`);
        setStrategies({ presets: r.data.presets || [], custom: [] });
      } catch { /* ignore */ }
    }
  };

  useEffect(() => { load(); }, []);

  const all = [
    ...strategies.presets.map(p => ({ ...p, kind: "preset" })),
    ...strategies.custom.map(c => ({ ...c, id: `custom_${c.strategy_id}`, kind: "custom" })),
  ];
  const current = all.find(s => s.id === value) || strategies.presets[0];

  const handlePick = (s) => {
    setOpen(false);
    onChange?.(s.id, s.weights, s.criteria || null);
  };

  const canWrite = isElite || meta.is_elite || meta.is_pro;
  const canCreateNew = isElite || meta.is_elite || meta.can_create;

  const openEditor = (existing = null) => {
    // Editing an existing strategy: allowed for any paid tier that owns it.
    // Creating a new one: Elite unlimited, Pro capped at 1, Free upsell.
    const allowed = existing ? canWrite : canCreateNew;
    if (!allowed) {
      setOpen(false);
      if (!canWrite) {
        // Free tier → full upsell flow.
        if (onUpgrade) onUpgrade("elite:custom_scoring");
        else navigate("/pricing");
      } else {
        // Pro at cap → Elite upsell.
        if (onUpgrade) onUpgrade("elite:custom_scoring");
        else navigate("/pricing");
      }
      return;
    }
    setEditing(existing || { name: "", description: "", weights: current?.weights });
    setDialogOpen(true);
    setOpen(false);
  };

  const handleSaved = (saved) => {
    setDialogOpen(false);
    setEditing(null);
    load().then(() => {
      onChange?.(`custom_${saved.strategy_id}`, saved.weights, saved.criteria || null);
    });
  };

  const handleDeleted = (strategy_id) => {
    setDialogOpen(false);
    setEditing(null);
    load().then(() => {
      if (value === `custom_${strategy_id}`) {
        onChange?.("balanced", strategies.presets.find(p => p.id === "balanced")?.weights, null);
      }
    });
  };

  return (
    <>
      <div className="relative" data-testid="strategy-picker">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => setOpen(o => !o)}
          data-testid="strategy-picker-toggle"
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <span className="hidden sm:inline">Strategy:</span>
          <span className="font-medium">{current?.name || "Balanced"}</span>
          {!isElite && <Badge className="text-[9px] px-1 py-0 bg-amber-500/15 text-amber-500 border-0 ml-1">Elite</Badge>}
          <ChevronDown className="w-3 h-3" />
        </Button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full mt-1 w-72 z-50 rounded-lg border border-border bg-card shadow-2xl overflow-hidden" data-testid="strategy-picker-menu">
              <div className="px-3 py-2 border-b border-border bg-secondary/40">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  Preset Strategies
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {strategies.presets.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handlePick(p)}
                    className={`w-full text-left px-3 py-2 hover:bg-secondary/50 transition-colors ${value === p.id ? "bg-amber-500/10" : ""}`}
                    data-testid={`strategy-option-${p.id}`}
                  >
                    <p className="text-xs font-semibold text-foreground">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.description}</p>
                  </button>
                ))}

                {strategies.custom.length > 0 && (
                  <>
                    <div className="px-3 py-2 border-t border-b border-border bg-secondary/40 mt-1">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                        Your Custom Strategies
                      </p>
                    </div>
                    {strategies.custom.map(c => {
                      const id = `custom_${c.strategy_id}`;
                      return (
                        <div key={c.strategy_id} className={`flex items-center hover:bg-secondary/50 transition-colors ${value === id ? "bg-amber-500/10" : ""}`}>
                          <button
                            onClick={() => handlePick({ ...c, id, weights: c.weights })}
                            className="flex-1 text-left px-3 py-2"
                            data-testid={`strategy-option-${c.strategy_id}`}
                          >
                            <p className="text-xs font-semibold text-foreground flex items-center gap-1">
                              <Crown className="w-3 h-3 text-amber-500" /> {c.name}
                            </p>
                            {c.description && <p className="text-[10px] text-muted-foreground">{c.description}</p>}
                          </button>
                          <button
                            onClick={() => openEditor(c)}
                            className="p-2 text-muted-foreground hover:text-foreground"
                            title="Edit"
                            data-testid={`strategy-edit-${c.strategy_id}`}
                          >
                            <SlidersIcon className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              <div className="border-t border-border">
                <button
                  onClick={() => openEditor(null)}
                  className="w-full text-left px-3 py-2.5 hover:bg-amber-500/10 transition-colors flex items-center gap-2"
                  data-testid="strategy-create-btn"
                >
                  {canCreateNew ? (
                    <Plus className="w-3.5 h-3.5 text-amber-500" />
                  ) : (
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                  <span className="text-xs font-medium">
                    Create Custom Strategy
                  </span>
                  {!canCreateNew && (
                    <Badge className="text-[9px] px-1 py-0 bg-amber-500/15 text-amber-500 border-0 ml-auto">
                      {meta.is_pro ? "Elite for more" : "Pro / Elite"}
                    </Badge>
                  )}
                </button>
                {meta.is_pro && !meta.is_elite && (
                  <p className="px-3 pb-2 text-[9px] text-muted-foreground">
                    Pro includes 1 custom strategy · Elite unlimited
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] bg-card border-border" data-testid="strategy-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              {editing?.strategy_id ? "Edit Strategy" : "Create Custom Strategy"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Set your own weights. The Deal Score for every property in the app will be
              recalculated against your strategy — including Bulk Analyze results.
            </DialogDescription>
          </DialogHeader>
          <StrategyEditor
            initial={editing}
            onSave={handleSaved}
            onCancel={() => { setDialogOpen(false); setEditing(null); }}
            onDelete={handleDeleted}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
